<template>
  <v-app>
    <v-main>
      <v-container fluid>
        <div>
          <!-- Antet standard de pagină (ca /hr-dashboard): titlu + acțiuni în slot -->
          <NcrmHomePageHeader icon="mdi-account-group-outline" :title="$t('users.title')">
            <v-text-field
              v-model="search"
              class="users-search"
              density="compact"
              prepend-inner-icon="mdi-magnify"
              :label="$t('users.search')"
              hide-details
              clearable
            />
            <!-- CTA constructiv principal al suprafeței (un singur buton tertiary plin) -->
            <v-btn color="tertiary" prepend-icon="mdi-plus" @click="openAddDialog">
              {{ $t("users.add") }}
            </v-btn>
          </NcrmHomePageHeader>

          <v-progress-linear v-if="loading && loaded" indeterminate />

          <!-- Comutator vizualizare (carduri / tabel) — standard NcrmSegmentedControl -->
          <div class="users-subbar">
            <NcrmSegmentedControl
              :model-value="viewMode"
              :items="viewItems"
              :aria-label="$t('users.view.ariaLabel')"
              @update:model-value="viewMode = $event"
            />
          </div>

          <!-- Eroarea de încărcare nu ascunde datele deja încărcate: lista rămâne, cu ieșire (Reîncearcă). -->
          <AppErrorState v-if="loadError" class="mb-4" :message="$t('users.loadError')" @retry="fetchUsers" />

          <AppEmptyState
            v-if="showEmptyState"
            :icon="search ? 'mdi-account-search-outline' : 'mdi-account-group-outline'"
            :title="search ? $t('users.emptySearch') : $t('users.empty')"
            :description="search ? $t('users.emptySearchHint') : ''"
          >
            <v-btn v-if="search" variant="outlined" @click="search = ''">{{ $t("users.clearSearch") }}</v-btn>
            <v-btn v-else color="tertiary" prepend-icon="mdi-plus" @click="openAddDialog">{{ $t("users.add") }}</v-btn>
          </AppEmptyState>

          <v-skeleton-loader v-else-if="loading && !loaded" type="paragraph, paragraph, paragraph" />

          <UserList
            v-else-if="filteredRecords.length"
            :items="filteredRecords"
            :view-mode="viewMode"
            :columns="columns"
            :format-date="formatDate"
            @edit="openEditDialog"
            @roles="goToRolesPage"
            @delete="confirmDeleteUser"
          />
        </div>

        <!-- Drawer adăugare/editare utilizator (pattern operațional: popup din dreapta) -->
        <UserFormDrawer
          v-model="editDialog"
          :is-edit="isEdit"
          :user="editRecord"
          :role-options="roleOptions"
          :company-options="companyOptions"
          :show-company="isAdmin"
          :error="formError"
          @save="onDrawerSave"
        />

        <!-- Dialog confirmare ștergere (tastare DELETE pentru date critice) -->
        <v-dialog v-model="deleteDialog" max-width="480">
          <v-card rounded="lg">
            <v-card-title class="text-h6 pt-4">{{ $t("users.delete.title") }}</v-card-title>
            <v-card-text>
              <p class="mb-4">{{ $t("users.delete.body") }}</p>
              <v-text-field
                v-model="deleteConfirmText"
                :label="$t('users.delete.typeToConfirm')"
                autofocus
                hide-details
                @keyup.enter="performDelete"
              />
              <!-- Eroarea rămâne în dialog (cu contextul operației), nu în snackbar. -->
              <v-alert v-if="deleteError" type="error" density="compact" class="mt-3">{{ deleteError }}</v-alert>
            </v-card-text>
            <v-card-actions class="px-6 pb-4 pt-0">
              <v-spacer />
              <v-btn variant="text" :disabled="deleteLoading" @click="closeDeleteDialog">{{ $t("users.delete.cancel") }}</v-btn>
              <v-btn
                color="error"
                variant="flat"
                :disabled="deleteConfirmText !== 'DELETE'"
                :loading="deleteLoading"
                @click="performDelete"
              >
                {{ $t("users.delete.confirm") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";
import AppEmptyState from "@/components/app/AppEmptyState.vue";
import AppErrorState from "@/components/app/AppErrorState.vue";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import NcrmSegmentedControl from "@/components/ncrm/shared/NcrmSegmentedControl.vue";
import UserFormDrawer from "@/components/userAccount/UserFormDrawer.vue";
import UserList from "@/components/userAccount/UserList.vue";
import { buildUserPayload } from "@/components/userAccount/usersPayload.mjs";
import { notifyMutation } from "@/Utile/mutationFeedback.mjs";

export default defineComponent({
  name: "Users",
  components: { AppEmptyState, AppErrorState, NcrmHomePageHeader, NcrmSegmentedControl, UserFormDrawer, UserList },
  data() {
    return {
      usersData: [],
      editDialog: false,
      isEdit: false,
      editRecord: {
        id: null,
        email: "",
        password: "",
        name: "",
        role: "",
        company_id: "",
      },
      formError: "",
      search: "",
      viewMode: "list", // sau 'cards'
      roleValues: [
        "admin", "miniadmin", "useradmin-default",
        "vacation-default", "vacation-user", "vacation-manager", "vacation-director", "vacation-hr",
        "crm-default", "crm-operator", "crm-caller",
        "recruiter-default", "recruiter-hr", "recruiter-viewer",
      ],
      companyOptions: [],
      // Notificări + confirmare ștergere (tastare DELETE)
      loaded: false,
      loadError: false,
      deleteDialog: false,
      deleteId: null,
      deleteConfirmText: "",
      deleteLoading: false,
      deleteError: "",
      loading: false,
    };
  },
  computed: {
    roleOptions() {
      return this.roleValues.map((value) => ({ label: value, value }));
    },
    viewItems() {
      return [
        { value: "cards", label: this.$t("users.view.cards"), icon: "mdi-view-grid-outline" },
        { value: "list", label: this.$t("users.view.table"), icon: "mdi-view-list-outline" },
      ];
    },
    columns() {
      return [
        { key: "email", title: this.$t("users.fields.email") },
        { key: "name", title: this.$t("users.fields.name") },
        { key: "role", title: this.$t("users.fields.role") },
        { key: "company", title: this.$t("users.fields.companyShort") },
        { key: "created_at", title: this.$t("users.createdAt") },
        { key: "updated_at", title: this.$t("users.updatedAt") },
        { key: "actions", title: this.$t("users.actions") },
      ];
    },
    // „Gol” numai după o încărcare reușită; la eroare se vede starea de eroare, nu „nu există utilizatori”.
    showEmptyState() {
      return this.loaded && !this.loadError && this.filteredRecords.length === 0;
    },
    filteredRecords() {
      let records = this.usersData;
      if (this.search) {
        const searchTerm = this.search.toLowerCase();
        records = records.filter((item) =>
          Object.values(item)
            .filter(val => val)
            .some((val) => String(val).toLowerCase().includes(searchTerm))
        );
      }
      return records;
    },
    defaultRole() {
      const preferred = this.roleOptions.find((r) => r.value === "vacation-default");
      return preferred ? preferred.value : this.roleOptions[0]?.value || "";
    },
    isAdmin() {
      const role = (localStorage.getItem("user-role") || "").toLowerCase();
      return role === "admin";
    },
    isMiniAdmin() {
      const primary = (localStorage.getItem("user-role") || "").toLowerCase();
      if (primary === "miniadmin") return true;
      try {
        const roles = JSON.parse(localStorage.getItem("user-roles") || "[]");
        return roles
          .map((r) => (typeof r === "string" ? r.toLowerCase() : (r?.key || r?.role || "").toLowerCase()))
          .includes("miniadmin");
      } catch (_) {
        return false;
      }
    },
    userCompanyId() {
      const val = localStorage.getItem("user-company-id");
      return val ? Number(val) : null;
    },
  },
  created() {
    this.fetchUsers();
    this.fetchCompanies();
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const offset = date.getTimezoneOffset();
      date.setMinutes(date.getMinutes() - offset);
      return date.toISOString().split("T")[0];
    },
    notify(message, type = "success") {
      notifyMutation(message, type);
    },
    openAddDialog() {
      this.isEdit = false;
      this.formError = "";
      this.editRecord = {
        id: null,
        email: "",
        password: "",
        name: "",
        role: this.defaultRole,
        company_id: "",
      };
      this.editDialog = true;
    },
    openEditDialog(user) {
      this.isEdit = true;
      this.formError = "";
      // Nu preluăm parola din backend; dacă se dorește schimbare se va introduce o nouă parolă
      this.editRecord = { ...user, password: "", company_id: user.company_id || "" };
      this.editDialog = true;
    },
    // Drawerul emite forma editată; construirea payload-ului rămâne aici (scoping companie).
    onDrawerSave(form) {
      this.editRecord = { ...this.editRecord, ...form };
      if (this.isEdit) this.updateUser();
      else this.addUser();
    },
    addUser() {
      const payload = buildUserPayload(this.editRecord, {
        isAdmin: this.isAdmin,
        isMiniAdmin: this.isMiniAdmin,
        userCompanyId: this.userCompanyId,
        isEdit: false,
      });
      // Pentru crearea unui utilizator, parola este obligatorie
      if (!payload.email || !payload.password) {
        this.formError = this.$t("users.alerts.missingEmailPassword");
        return;
      }
      this.formError = "";
      axios
        .post("/users", payload)
        .then((response) => {
          this.editDialog = false;
          this.notify(response?.data?.message || this.$t("app.actions.save"), "success");
          this.fetchUsers();
        })
        .catch((error) => {
          console.error("Eroare la adăugarea utilizatorului:", error);
          this.formError = this.$t("users.alerts.addError");
        });
    },
    updateUser() {
      if (!this.editRecord.id) {
        this.formError = this.$t("users.alerts.missingId");
        return;
      }
      this.formError = "";
      const payload = buildUserPayload(this.editRecord, {
        isAdmin: this.isAdmin,
        isMiniAdmin: this.isMiniAdmin,
        userCompanyId: this.userCompanyId,
        isEdit: true,
      });
      axios
        .put(`/users/${this.editRecord.id}`, payload)
        .then((response) => {
          this.editDialog = false;
          this.notify(response?.data?.message || this.$t("app.actions.save"), "success");
          this.fetchUsers();
        })
        .catch((error) => {
          console.error("Eroare la actualizarea utilizatorului:", error);
          this.formError = this.$t("users.alerts.updateError");
        });
    },
    confirmDeleteUser(id) {
      this.deleteId = id;
      this.deleteConfirmText = "";
      this.deleteError = "";
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteId = null;
      this.deleteConfirmText = "";
      this.deleteError = "";
    },
    // Dialogul rămâne deschis cât durează cererea; la eșec rămâne cu eroare inline, la succes se închide.
    performDelete() {
      if (this.deleteConfirmText !== "DELETE" || !this.deleteId || this.deleteLoading) return;
      this.deleteLoading = true;
      this.deleteError = "";
      return axios
        .delete(`/users/${this.deleteId}`)
        .then(() => {
          this.closeDeleteDialog();
          this.notify(this.$t("users.alerts.deleteSuccess"), "success");
          this.fetchUsers();
        })
        .catch((error) => {
          console.error("Eroare la ștergerea utilizatorului:", error);
          this.deleteError = this.$t("users.alerts.deleteError");
        })
        .finally(() => {
          this.deleteLoading = false;
        });
    },
    fetchUsers() {
      this.loading = true;
      return axios
        .get("/users")
        .then((response) => {
          this.usersData = Array.isArray(response.data) ? response.data : [];
          this.loadError = false;
          this.loaded = true;
        })
        .catch((error) => {
          console.error("Eroare la obținerea utilizatorilor:", error);
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchCompanies() {
      axios
        .get("/candidati/companies")
        .then((response) => {
          const data = Array.isArray(response.data) ? response.data : [];
          this.companyOptions = data.map((c) => ({
            id: c.id,
            label: `${c.company_name || c.name || c.company || "Companie"} (${c.id})`,
          }));
        })
        .catch((error) => {
          console.error("Eroare la obținerea companiilor:", error);
        });
    },
    goToRolesPage(user) {
      if (!user || !user.id) return;
      this.$router.push({ name: "UserRoles", params: { id: user.id } });
    },
  },
});
</script>

<style scoped>
.users-search {
  flex: 1 1 220px;
  min-width: 220px;
  max-width: 320px;
}
/* Comutatorul Carduri/Tabel singur sub antet: aliniat la stânga (docs/design/patterns.md). */
.users-subbar {
  display: flex;
  justify-content: flex-start;
  padding: 12px 0;
}
</style>
